import { StanModal } from '@/components';
import { useRef } from 'react';
import { ProductPayment } from '../types/transactionTypes';
import { fulfillOrder } from '@/lib/api/income/api';
import { SENTRY_FEATURES, stanNotify } from '@/lib';
import { StanFileDropZone } from '@/components/index';
import { StanFileDropZoneRef } from '@/components/core-components/types';
import IcPerson from '@/assets/icons/ic-profile.svg';
import IcSpeechBubble from '@/assets/icons/ic-message.svg';
import IcFileUpload from '@/assets/icons/ic-file-upload.svg';
import IcDelete from '@/assets/icons/ic-trash.svg';
import IcFile from '@/assets/icons/ic-file.svg';
import { FILE_UPLOAD_FLAGS, useFulfillOrderModal } from '../hooks/useFulfillOrderModal';

interface FulfillOrderModalProps {
    isOpen: boolean;
    onClose: () => void;
    onOrderFulfilled: () => void;
    transactionData: ProductPayment;
    orderId?: string | number;
}

export function FulfillOrderModal({
    isOpen,
    onClose,
    transactionData,
    orderId,
    onOrderFulfilled,
}: FulfillOrderModalProps) {
    const fileDropZoneRef = useRef<StanFileDropZoneRef>(null);
    const { flag, fileUploadProgress, fileName, fileData, resetDownload, uploadingFile, addFile, onUploadProgress } =
        useFulfillOrderModal();

    const customerName = transactionData.data.name;
    const productTitle = transactionData.data.title;

    return (
        <StanModal
            id='order-fulfillment-modal'
            isOpen={isOpen}
            onClose={onClose}
            title='Fulfill Your Order'
            primaryActionLabel='Fulfill Order'
            aria-label='Fulfill order by uploading files'
            onPrimaryAction={() => {
                fulfillOrder(orderId, fileData)
                    .then(() => {
                        resetDownload();
                        onOrderFulfilled();
                        stanNotify({
                            type: 'success',
                            title: 'Great!',
                            duration: 2000,
                            text: `This order is on its way to ${customerName}`,
                        });
                    })
                    .catch(error => {
                        stanNotify({
                            type: 'error',
                            title: 'Something went wrong',
                            duration: 2000,
                            text: 'Please reach out to us at friends@stanwith.me.',
                            error: {
                                message: '[INCOME] failed-to-fulfill-order',
                                options: {
                                    tags: { feature: SENTRY_FEATURES.INCOME },
                                    context: { error },
                                },
                            },
                        });
                    })
                    .finally(() => {
                        onClose();
                    });
            }}
            primaryActionDisabled={flag === FILE_UPLOAD_FLAGS.LOADING}
        >
            <StanFileDropZone
                ref={fileDropZoneRef}
                onFilesChanged={uploadingFile}
                onProgress={onUploadProgress}
                objectType='only_max'
                onError={error => {
                    stanNotify({
                        type: 'error',
                        title: 'Something went wrong',
                        text: error.message,
                    });
                }}
                ghost
            />
            <div>
                {productTitle !== '' && (
                    <div className='w-full'>
                        <h6 className='text-dark text-sm font-bold'>{productTitle}</h6>
                    </div>
                )}
                <div className='mt-2 flex w-full'>
                    {customerName !== '' && (
                        <div className='text-light flex items-center text-xs font-semibold'>
                            <IcPerson className='mr-2 size-4' />
                            {customerName}
                        </div>
                    )}
                    {transactionData.data.note !== '' && transactionData.data.note !== null && (
                        <span className='text-light flex items-center text-xs font-semibold'>
                            <IcSpeechBubble className='mr-2 size-4' />
                            {transactionData.data.note}
                        </span>
                    )}
                </div>
                <div className='border-neutral-dark text-dark mx-auto my-5 mb-3 w-full rounded-sm border border-dashed p-8 text-center text-xs font-bold'>
                    <div className='flex flex-wrap justify-center'>
                        <div>
                            <div className='mt-4'>
                                <a
                                    className='mb-5 inline-flex cursor-pointer items-center rounded-md border-2 px-2 py-0 text-center text-sm font-bold no-underline!'
                                    onClick={() => {
                                        addFile();
                                        fileDropZoneRef.current?.clearFile();
                                        fileDropZoneRef.current?.openFilePicker();
                                    }}
                                >
                                    <IcFileUpload className='mr-2' />
                                    Upload Files
                                </a>
                            </div>
                            <div className='text-light mx-auto mb-2 w-72 text-center'>
                                If you are uploading multiple files for this order, please combine them into a single
                                .zip file and then upload it!
                            </div>
                        </div>
                    </div>
                </div>
                {/* File preview logic */}
                {(fileData || flag === FILE_UPLOAD_FLAGS.LOADING) && (
                    <div className='text-light border-neutral-dark flex w-full items-center rounded-lg border p-2 text-sm'>
                        <span className='file-name flex w-full items-center text-left'>
                            <IcFile className='mr-2' />
                            <span>{fileName}</span>
                        </span>
                        {/* Uploading state */}
                        {flag === FILE_UPLOAD_FLAGS.LOADING && (
                            <div className='circle-progress-bar text-right'>
                                <div className='float-right flex w-full items-center justify-self-end'>
                                    <span
                                        className='mr-2 font-semibold'
                                        aria-live='polite'
                                        aria-label='Upload progress'
                                    >
                                        {fileUploadProgress}%
                                    </span>
                                </div>
                            </div>
                        )}
                        {/* Uploaded state */}
                        {flag === FILE_UPLOAD_FLAGS.UPLOADED && (
                            <div className='col-auto flex items-center px-0'>
                                <span className='text-seafoam font-bold'>Uploaded</span>
                                <IcDelete className='ml-2 cursor-pointer' onClick={resetDownload} />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </StanModal>
    );
}
